import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import BottomNav from './components/BottomNav';
import DrawerMenu from './components/DrawerMenu';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import Dashboard from './pages/Dashboard';
import ProfilePage from './pages/ProfilePage';
import LeadsPage from './pages/LeadsPage';
import CustomersPage from './pages/CustomersPage';
import InvoicesPage from './pages/InvoicesPage';
import CreateInvoicePage from './pages/CreateInvoicePage';
import PaymentsPage from './pages/PaymentsPage';
import PublicProfilePage from './pages/PublicProfilePage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import HelpPage from './pages/HelpPage';
import MarketSourcePage from './pages/MarketSourcePage';
import PremiumPage from './pages/PremiumPage';
import PremiumLock from './components/PremiumLock';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header with Hamburger Drawer */}
      <DrawerMenu />
      
      {/* Page Content - Add left margin on desktop for sidebar */}
      <main className="pb-20 md:pb-6 md:ml-64">
        {children}
      </main>
      
      {/* Mobile Bottom Nav */}
      <BottomNav />
    </div>
  );
};

// Premium Protected Route - requires subscription
const PremiumProtectedRoute = ({ children, feature }) => {
  const { user, loading } = useAuth();
  const isPremium = localStorage.getItem('is_premium') === 'true';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // If not premium, show lock screen
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <DrawerMenu />
        <div className="md:ml-64">
          <PremiumLock feature={feature} />
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <DrawerMenu />
      <main className="pb-20 md:pb-6 md:ml-64">
        {children}
      </main>
      <BottomNav />
    </div>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route
              path="/auth"
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              }
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <LeadsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <PremiumProtectedRoute feature="Customer Management">
                  <CustomersPage />
                </PremiumProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/create"
              element={
                <ProtectedRoute>
                  <CreateInvoicePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <PremiumProtectedRoute feature="Payment Tracking">
                  <PaymentsPage />
                </PremiumProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <HelpPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/market-source"
              element={
                <ProtectedRoute>
                  <MarketSourcePage />
                </ProtectedRoute>
              }
            />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/designer/:designerId" element={<PublicProfilePage />} />
            <Route path="/profile/:designerId" element={<PublicProfilePage />} />
            {/* Short URL: designbiz.in/username */}
            <Route path="/:designerId" element={<PublicProfilePage />} />
          </Routes>
          <Toaster position="top-center" />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
